import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';

import { Layout } from '@/components/Layout';
import { PrivateRoute } from '@/components/PrivateRoute';
import { RoleGate } from '@/components/RoleGate';
import { Skeleton } from '@/components/ui/skeleton';
import { LoginPage } from '@/pages/Login';
import { NotFoundPage } from '@/pages/NotFound';
import { OnboardingPage } from '@/pages/Onboarding';
import { useAuthStore } from '@/stores/auth.store';

/**
 * Roteamento (Sprint 16 — D16): páginas analíticas + /pendencias lazy.
 * - `/dashboard`            → adaptativo (Gestor vê o gráfico via lazy chunk).
 * - `/relatorios/usuario`   → só Gestor (RoleGate).
 * - `/relatorios/periodo`   → só Gestor.
 * - `/os/:id/analise`       → só Gestor.
 * - `/pendencias`           → qualquer autenticado.
 */

const DashboardPage = lazy(() =>
  import('@/pages/Dashboard').then((m) => ({ default: m.DashboardPage })),
);
const OsPage = lazy(() => import('@/pages/Os').then((m) => ({ default: m.OsPage })));
const OsDetalhePage = lazy(() =>
  import('@/pages/OsDetalhe').then((m) => ({ default: m.OsDetalhePage })),
);
const OsAnalisePage = lazy(() =>
  import('@/pages/OsAnalise').then((m) => ({ default: m.OsAnalisePage })),
);
const RelatorioUsuarioPage = lazy(() =>
  import('@/pages/RelatorioUsuario').then((m) => ({ default: m.RelatorioUsuarioPage })),
);
const RelatorioPeriodoPage = lazy(() =>
  import('@/pages/RelatorioPeriodo').then((m) => ({ default: m.RelatorioPeriodoPage })),
);
const PendenciasPage = lazy(() =>
  import('@/pages/Pendencias').then((m) => ({ default: m.PendenciasPage })),
);
const ApontamentoPage = lazy(() =>
  import('@/pages/Apontamento').then((m) => ({ default: m.ApontamentoPage })),
);
const ChatPage = lazy(() => import('@/pages/Chat').then((m) => ({ default: m.ChatPage })));
const ClientesPage = lazy(() =>
  import('@/pages/Clientes').then((m) => ({ default: m.ClientesPage })),
);
const UsuariosPage = lazy(() =>
  import('@/pages/Usuarios').then((m) => ({ default: m.UsuariosPage })),
);
const PerfilPage = lazy(() => import('@/pages/Perfil').then((m) => ({ default: m.PerfilPage })));

function RootRedirect() {
  // P1-Auth: presença de `usuario` no store reflete sessão local. A
  // validação efetiva do cookie httpOnly acontece em `<PrivateRoute>`.
  const isLogged = !!useAuthStore((s) => s.usuario);
  return <Navigate to={isLogged ? '/dashboard' : '/login'} replace />;
}

function PageSkeleton() {
  return (
    <div className="space-y-3 p-6" data-testid="page-skeleton">
      <Skeleton className="h-8 w-1/3" />
      <Skeleton className="h-4 w-1/4" />
      <Skeleton className="h-64 w-full" />
    </div>
  );
}

function Lazy({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<PageSkeleton />}>{children}</Suspense>;
}

export const router = createBrowserRouter([
  { path: '/', element: <RootRedirect /> },
  { path: '/onboarding', element: <OnboardingPage /> },
  { path: '/login', element: <LoginPage /> },
  {
    element: (
      <PrivateRoute>
        <Layout />
      </PrivateRoute>
    ),
    children: [
      {
        path: '/dashboard',
        element: (
          <Lazy>
            <DashboardPage />
          </Lazy>
        ),
      },
      {
        path: '/os',
        element: (
          <Lazy>
            <OsPage />
          </Lazy>
        ),
      },
      {
        path: '/os/:id',
        element: (
          <Lazy>
            <OsDetalhePage />
          </Lazy>
        ),
      },
      {
        path: '/os/:id/analise',
        element: (
          <RoleGate papeisPermitidos={['GESTOR']}>
            <Lazy>
              <OsAnalisePage />
            </Lazy>
          </RoleGate>
        ),
      },
      {
        path: '/relatorios/usuario',
        element: (
          <RoleGate papeisPermitidos={['GESTOR']}>
            <Lazy>
              <RelatorioUsuarioPage />
            </Lazy>
          </RoleGate>
        ),
      },
      {
        path: '/relatorios/periodo',
        element: (
          <RoleGate papeisPermitidos={['GESTOR']}>
            <Lazy>
              <RelatorioPeriodoPage />
            </Lazy>
          </RoleGate>
        ),
      },
      {
        path: '/pendencias',
        element: (
          <Lazy>
            <PendenciasPage />
          </Lazy>
        ),
      },
      {
        // Sprint 20 — sem RoleGate; a página decide internamente o que mostrar
        // (operacional: só "Meus Apontamentos"; Gestor: + "Configurações").
        path: '/apontamento',
        element: (
          <Lazy>
            <ApontamentoPage />
          </Lazy>
        ),
      },
      {
        // Sprint 22 — chat interno. Todos os perfis acessam; broadcast só
        // Gestor envia (controle interno na página).
        path: '/chat',
        element: (
          <Lazy>
            <ChatPage />
          </Lazy>
        ),
      },
      {
        path: '/clientes',
        element: (
          <RoleGate papeisPermitidos={['GESTOR']}>
            <Lazy>
              <ClientesPage />
            </Lazy>
          </RoleGate>
        ),
      },
      {
        path: '/usuarios',
        element: (
          <RoleGate papeisPermitidos={['GESTOR']}>
            <Lazy>
              <UsuariosPage />
            </Lazy>
          </RoleGate>
        ),
      },
      {
        path: '/perfil',
        element: (
          <Lazy>
            <PerfilPage />
          </Lazy>
        ),
      },
    ],
  },
  { path: '*', element: <NotFoundPage /> },
]);
